import {Box, Checkbox, IconButton, TableCell, TableHead, TableRow, TableSortLabel, Tooltip} from "@mui/material";
import React from "react";
import {visuallyHidden} from "@mui/utils";
import {Link, useSearchParams} from "react-router-dom";
import {ColumnDef, FilterDef, Order} from "./types";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import {useTranslation} from "react-i18next";
import {BaseModelType} from "../../app/api/types";
import {Info as InfoIcon} from "@mui/icons-material";

interface ExTableHeadProps<T extends BaseModelType> {
    numSelected: number;
    onSelectAllClick?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    order: Order;
    orderBy?: string;
    rowCount: number;
    columnDefs: ColumnDef<T>[];
    filterDefs?: FilterDef<T>[];
    onFilterButtonClick: (columnName: string) => void;
    selectableRows: boolean;
}

export default function ExTableHead<T extends BaseModelType>(props: ExTableHeadProps<T>) {
    const {t} = useTranslation();
    const {
        onSelectAllClick,
        order,
        orderBy,
        numSelected,
        rowCount,
        columnDefs,
        filterDefs,
        onFilterButtonClick,
        selectableRows,
    } = props;
    const [searchParams] = useSearchParams();

    return (
        <TableHead>
            <TableRow>
                {onSelectAllClick && (
                    <TableCell
                        sx={selectableRows && columnDefs[0].sticky ? {position: "sticky", zIndex: 4, left: 0} : {}}
                        padding="none"
                    >
                        <Checkbox
                            color="primary"
                            indeterminate={numSelected > 0 && numSelected < rowCount}
                            checked={rowCount > 0 && numSelected === rowCount}
                            onChange={onSelectAllClick}
                        />
                    </TableCell>
                )}
                {columnDefs.map((columnDef, index) => {
                    const sortSearchParams = new URLSearchParams(searchParams);
                    let direction = "";
                    if (orderBy === columnDef.fieldName) {
                        direction = order === "asc" ? "-" : "";
                    }
                    sortSearchParams.set("sort", `${direction}${String(columnDef.fieldName)}`);
                    sortSearchParams.set("page", "0");

                    const filterButton = filterDefs && filterDefs.find((x) => x.fieldName === columnDef.fieldName) && (
                        <Tooltip title={t("action_filterByThisColumn")}>
                            <IconButton
                                sx={{color: "grey.500", ":hover": {color: "grey.600"}}}
                                aria-label="filter"
                                size="small"
                                onClick={(
                                    event: React.MouseEvent<HTMLAnchorElement> | React.MouseEvent<HTMLButtonElement>
                                ) => {
                                    event.preventDefault();
                                    onFilterButtonClick(String(columnDef.fieldName));
                                }}
                            >
                                <FilterAltIcon fontSize="inherit" />
                            </IconButton>
                        </Tooltip>
                    );

                    const isSorted = orderBy === columnDef.fieldName;

                    return (
                        <TableCell
                            sx={{
                                width: index === 0 && columnDef.width ? columnDef.width + 24 : columnDef.width,
                                border: 1,
                                borderLeft: index === 0 ? 0 : undefined,
                                borderRight: index === columnDefs.length - 1 ? 0 : undefined,
                                borderColor: "grey.300",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                pl: index > 0 ? 2 : 5,
                                ...(columnDef.sticky
                                    ? {
                                          position: "sticky",
                                          zIndex: 5,
                                          ...(selectableRows ? {left: 42} : {left: 0}),
                                      }
                                    : {}),
                            }}
                            key={String(columnDef.fieldName)}
                            align={columnDef.align}
                            sortDirection={orderBy === columnDef.fieldName ? order : false}
                            title={columnDef.label}
                        >
                            <Box
                                sx={{
                                    display: "flex",
                                    flexDirection: "inherit",
                                    overflow: "hidden",
                                    justifyContent: columnDef.align === "center" ? "center" : undefined,
                                }}
                            >
                                <TableSortLabel
                                    component={Link}
                                    to={`?${sortSearchParams.toString()}`}
                                    active={orderBy === columnDef.fieldName}
                                    direction={orderBy === columnDef.fieldName ? order : "asc"}
                                    disabled={!columnDef.sortable}
                                    sx={{
                                        flexGrow: columnDef.align !== "center" ? 1 : undefined,
                                        display: "flex",
                                        overflow: "hidden",
                                        whiteSpace: "nowrap",
                                        textOverflow: "ellipsis",
                                        justifyContent: columnDef.align !== "center" ? "space-between" : undefined,
                                    }}
                                    hideSortIcon={true}
                                >
                                    <Box
                                        sx={{
                                            overflow: "hidden",
                                            whiteSpace: "nowrap",
                                            textOverflow: "ellipsis",
                                            display: "flex",
                                            textAlign: columnDef.align,
                                        }}
                                    >
                                        {columnDef.label}
                                    </Box>
                                </TableSortLabel>
                                {columnDef.headerInfo ? (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexDirection: "inherit",
                                            overflow: "hidden",
                                            justifyContent: columnDef.align === "center" ? "center" : undefined,
                                        }}
                                    >
                                        <Tooltip title={columnDef.headerInfo}>
                                            <InfoIcon color="primary" />
                                        </Tooltip>
                                    </Box>
                                ) : null}
                                {isSorted ? (
                                    <Box component="span" sx={{...visuallyHidden}}>
                                        {order === "desc" ? "sorted descending" : "sorted ascending"}
                                    </Box>
                                ) : null}
                                {filterButton}
                            </Box>
                        </TableCell>
                    );
                })}
            </TableRow>
        </TableHead>
    );
}
